import React, { FunctionComponent } from 'react';
import Box from '@material-ui/core/Box';
import { ISchemaFieldProps } from '../../model/IJsonSchema';
import { Checkbox, Typography, useTheme } from '@material-ui/core';
import checkboxesStyles from './customCheckboxesStyles';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import RadioButtonUncheckedOutlinedIcon from '@material-ui/icons/RadioButtonUncheckedOutlined';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { ICheckoutTheme } from 'src/common/theme/ICheckoutTheme';
import Translation from '../../../../../common/components/presentation/Translation/Translation';
import useCheckoutContext from '../../../../../gql/common/hooks/useCheckoutContext';
import useJourneyStepsContext from '../../../../../gql/checkout4/hooks/useJourneyStepsContext';
import usePlatformConfig from '../../../../../common/react/usePlatformConfig';
import useNewDxpDigitalAssignmentEntryEvent from '../../../../../gql/common/hooks/useNewDxpDigitalAssignmentEntryEvents';
import { CheckBoxId } from '../../../../../common/enum/TradeInComponentId';
import {
  computeMonthly,
  getBookingDetailsCommonFields,
  getCheckoutStepsCommonFields,
  getEventDataCommonFields,
  getPurchaseOptionCommonFields,
  getRetailerCommonFields,
  getTradeInCommonFields,
  vproductCommonFields,
  removeHostNameFromUrl,
} from '../../../../../gql/common/utils/dxpDataLayerCommonFields';
import {
  DxpDataLayerEventNames,
  FormContextTradeInType,
} from '../../../../../../../common/utilities/customDatalayerInterface';
import { EventDataTypes } from '../../../../../gql/common/enum/DataLayerEnums';

export const CustomCheckboxes: FunctionComponent<ISchemaFieldProps> = ({ formData }) => {
  const styles = checkboxesStyles();
  const {
    value,
    id,
    onChange,
    options: { enumOptions },
    rawErrors,
    formContext
  } = formData;
  const { checkoutLite } = useTheme<ICheckoutTheme>();
  const checkboxIconCircle = checkoutLite?.enquiryForm?.checkboxIconCircle;
  const selectedValues = new Set(value);
  const {
    platformConfig: { ga4DxpDataLayer },
  } = usePlatformConfig();
  const { deal } = useCheckoutContext();
  const { steps, activeStep } = useJourneyStepsContext();
  const { jlrDataLayer } = ga4DxpDataLayer ?? {};
  const { callDxpDigitalAssignmentEntryEvent } = useNewDxpDigitalAssignmentEntryEvent();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    selectedValues.has(e.target.name) ? selectedValues.delete(e.target.name) : selectedValues.add(e.target.name);
    onChange(Array.from(selectedValues));

    const label = Array.from(selectedValues).join(', ');

    if (deal && formData.id === CheckBoxId.MARKETING_OPTIONS_CHECKBOXES) {
      const { tradeIn } = formData.formContext;
      const { attributes, finance, car } = deal;
      const { amount, currency } = car?.prices.totalPrice?.[0]?.money || {};
      const { term, mileage } = finance?.formInput || {};
      const { reserveFee } = attributes.paymentFee || {};

      callDxpDigitalAssignmentEntryEvent(
        {
          ...getEventDataCommonFields(DxpDataLayerEventNames.TNC_PRIVACY_CTA_CLICK, true),
          event_action: removeHostNameFromUrl(window.location.href),
          event_category: EventDataTypes.CHECKOUT_CLICK,
          event_pagecategory: EventDataTypes.CHECKOUT,
          click_url: removeHostNameFromUrl(window.location.href),
          click_name: EventDataTypes.CHECKBOX,
          event_label: `${EventDataTypes.CHECKBOX}::${label ? label.toLowerCase() : ''}`,
          click_text: label?.toLowerCase() ?? '',
          vproduct: {
            ...vproductCommonFields(deal),
            total_weight: '',
            max_speed: '',
            est_delivery_time: deal.car?.deliveryTimeFrame?.toString() ?? '',
          },
          checkoutStep: {
            ...getCheckoutStepsCommonFields(steps, activeStep),
          },
          purchaseOption: {
            ...getPurchaseOptionCommonFields(deal),
            vehicle_cash_price: car?.prices.totalPrice?.[0]?.money ? `${currency} ${amount}`.toLowerCase() : '',
            terms: term ? `${term} months` : '',
            monthly_mileage: computeMonthly(mileage, 'km') ?? '',
            reservation_fee: reserveFee?.[0]?.amount.toString() ?? '',
            payment_period: deal.finance?.simulation?.loanTermPeriod
              ? `${deal.finance.simulation.loanTermPeriod.value} ${deal.finance.simulation.loanTermPeriod.unit}`
              : '',
            installment_fee: '',
            down_payment_trade_in_price: '',
            finance_product: deal.finance?.simulation?.financeProductName
              ? deal.finance.simulation.financeProductName.toLowerCase()
              : '',
            final_deferred_payment: '',
            total_to_pay: deal.car?.prices?.totalPrice?.[1]?.money?.amount.toString() ?? '',
          },
          tradein: {
            ...getTradeInCommonFields(deal, tradeIn?.tradeInCheckbox ? (tradeIn as FormContextTradeInType) : undefined),
          },
          retailer: {
            ...getRetailerCommonFields(deal),
          },
          bookingDetails: {
            ...getBookingDetailsCommonFields(deal, formContext),
          },
        },
        jlrDataLayer,
      );
    }
  };

  const checkboxes = enumOptions.map((option, index) => (
    <Box className={styles.checkboxContainer} key={index}>
      <Checkbox
        icon={checkboxIconCircle ? <RadioButtonUncheckedOutlinedIcon /> : <CheckBoxOutlineBlankIcon />}
        checkedIcon={checkboxIconCircle ? <CheckCircleIcon /> : <CheckBoxIcon />}
        color={'primary'}
        id={id}
        checked={selectedValues.has(option.value)}
        value={value}
        size={'small'}
        onChange={handleChange}
        data-test={`checkbox:${option.value}`}
        name={option.value}
        classes={{
          root: styles.root,
        }}
      />
      <Typography variant={'body2'} className={styles.label} data-test={`text:enquiryForm:${option.value}`}>
        <Translation id={option.label} />
      </Typography>
      <Typography className={styles.errorMessage}>{rawErrors && rawErrors[index]} </Typography>
    </Box>
  ));

  return <Box className={styles.checkboxesContainer}>{checkboxes}</Box>;
};

export default CustomCheckboxes;
